import { useCallback, useEffect, useRef } from "react";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;
const LOGOUT_LOCK_TTL_MS = 10 * 1000;

const LAST_ACTIVITY_KEY = "psig_last_activity";
const FORCE_LOGOUT_KEY = "psig_force_logout";
const LOGOUT_LOCK_KEY = "psig_idle_logout_lock";
const HANDLED_KEY = "psig_idle_handled";

const IDLE_MESSAGE =
    "Sua sessão foi encerrada por inatividade (5 minutos sem uso). Faça login novamente para continuar.";

export const touchIdleActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

export const clearIdleSessionKeys = () => {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(FORCE_LOGOUT_KEY);
    localStorage.removeItem(LOGOUT_LOCK_KEY);
    sessionStorage.removeItem(HANDLED_KEY);
};

const useIdleLogout = (user, logout) => {
    const logoutRef = useRef(logout);
    logoutRef.current = logout;

    const redirectAfterIdleAlert = useCallback(() => {
        if (sessionStorage.getItem(HANDLED_KEY)) return;

        sessionStorage.setItem(HANDLED_KEY, "1");
        window.alert(IDLE_MESSAGE);
        window.location.href = "/login";
    }, []);

    const performIdleLogout = useCallback(async () => {
        if (sessionStorage.getItem(HANDLED_KEY)) return;

        const now = Date.now();
        const lock = localStorage.getItem(LOGOUT_LOCK_KEY);

        if (lock && now - Number(lock) < LOGOUT_LOCK_TTL_MS) {
            return;
        }

        localStorage.setItem(LOGOUT_LOCK_KEY, String(now));
        sessionStorage.setItem(HANDLED_KEY, "1");

        window.alert(IDLE_MESSAGE);
        localStorage.setItem(FORCE_LOGOUT_KEY, String(now));

        try {
            await logoutRef.current();
        } catch (error) {
            console.log(error);
            window.location.href = "/login";
        }
    }, []);

    useEffect(() => {
        const onStorage = (event) => {
            if (event.key !== FORCE_LOGOUT_KEY || !event.newValue) return;
            redirectAfterIdleAlert();
        };

        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [redirectAfterIdleAlert]);

    useEffect(() => {
        if (!user) return undefined;

        touchIdleActivity();

        let lastMouseMove = 0;

        const onActivity = () => {
            touchIdleActivity();
        };

        const onMouseMove = () => {
            const now = Date.now();
            if (now - lastMouseMove < 1000) return;
            lastMouseMove = now;
            touchIdleActivity();
        };

        const activityEvents = [
            "mousedown",
            "keydown",
            "scroll",
            "click",
            "touchstart",
        ];

        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, onActivity, { passive: true });
        });
        window.addEventListener("mousemove", onMouseMove, { passive: true });

        const checkIdle = () => {
            const lastActivity = Number(
                localStorage.getItem(LAST_ACTIVITY_KEY) || 0
            );

            if (Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
                performIdleLogout();
            }
        };

        const intervalId = setInterval(checkIdle, CHECK_INTERVAL_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkIdle();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity);
            });
            window.removeEventListener("mousemove", onMouseMove);
            clearInterval(intervalId);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange
            );
        };
    }, [user, performIdleLogout]);
};

export default useIdleLogout;
